import { useEffect } from "react";

const events = [
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "pointerdown",
  "gesturestart",
  "mousemove"
];

type UseInactiveProps = {
  inactiveTimeout: number;
  onInactive: () => void;
  disabled?: boolean;
};

/**
 * Hook to monitor user inactivity and trigger a callback.
 *
 * @param inactiveTimeout - Duration in milliseconds before triggering inactivity.
 * @param onInactive - Callback executed when inactivity is detected.
 * @param disabled - When true, inactivity detection is disabled.
 */
export const useInactive = ({
  inactiveTimeout,
  onInactive,
  disabled = false
}: UseInactiveProps) => {
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (disabled) {
      // Clear any existing timer when disabled, this resets inactivity timer
      if (timer) {
        clearTimeout(timer);
      }
      return;
    }

    // Initiates the inactivity timer
    const startTimer = () => {
      timer = setTimeout(onInactive, inactiveTimeout);
    };

    // Resets the inactivity timer upon user activity
    const handleActivity = () => {
      clearTimeout(timer);
      startTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    startTimer();

    // Cleanup: remove event listeners and clear the timer
    return () => {
      clearTimeout(timer);
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [onInactive, inactiveTimeout, disabled]);
};
